import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import { Observable } from 'rxjs';

import { SignalRService } from 'src/app/features/signal-r';
import { delayedRetry } from 'src/app/operators';
import { RootStoreState } from 'src/app/store';

import {
    IdVerification, ParticipantVerification, UpdateParticipantApprovalResponse
} from '../models';
import { ParticipantVerificationStatus } from '../models/participant-verification-status.model';
import { ParticipantVerificationActions } from '../store/actions';

@Injectable()
export class ParticipantVerificationService {
  private listenerConfigured = false;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly store: Store<RootStoreState.State>,
    private readonly signalRService: SignalRService
  ) {}

  approveParticipant(
    participantVerification: ParticipantVerification
  ): Observable<UpdateParticipantApprovalResponse> {
    return this.httpClient
      .post<UpdateParticipantApprovalResponse>(
        `participantVerification/approval`,
        participantVerification
      )
      .pipe(delayedRetry(500, 3));
  }

  denyParticipant(participantVerification: ParticipantVerification) {
    return this.httpClient
      .post(`participantVerification/denial`, participantVerification)
      .pipe(delayedRetry(500, 3));
  }

  denyRejoiningParticipant(
    participantVerification: ParticipantVerification
  ): Observable<UpdateParticipantApprovalResponse> {
    return this.httpClient
      .post<UpdateParticipantApprovalResponse>(
        `participantVerification/rejoin/denial`,
        participantVerification
      )
      .pipe(delayedRetry(500, 3));
  }

  approveRejoiningParticipant(
    participantVerification: ParticipantVerification
  ): Observable<UpdateParticipantApprovalResponse> {
    return this.httpClient
      .post<UpdateParticipantApprovalResponse>(
        `participantVerification/rejoin/approval`,
        participantVerification
      )
      .pipe(delayedRetry(500, 3));
  }

  getIdVerifications(packageGuid: string) {
    return this.httpClient.get<IdVerification[]>(
      `participantVerification/getidverifications/${packageGuid}`
    );
  }

  getStatus(): Observable<ParticipantVerificationStatus> {
    return this.httpClient
      .get<ParticipantVerificationStatus>(`participantVerification/status`)
      .pipe(delayedRetry(500, 3));
  }

  public configureListeners() {
    if (!this.listenerConfigured) {
      this.listenerConfigured = true;
      this.signalRService.hubConnection.on('ReceiveAllSignersApproved', () => {
        this.store.dispatch(
          ParticipantVerificationActions.SetAllSignersApproved({
            payload: { allSignersApproved: true },
          })
        );
      });

      this.signalRService.hubConnection.on('ReceiveParticipantRejoinDenial', (data) => {
        this.store.dispatch(
          ParticipantVerificationActions.RejoiningParticipantDenied({
            packageUserGuid: data.packageUserGuid,
            deviceCode: data.sessionId,
            originalDeviceCode: data.originalSessionId
          })
        );
      });

      this.signalRService.hubConnection.on('ReceiveParticipantRejoinApproval', (data) => {
        this.store.dispatch(
          ParticipantVerificationActions.RejoiningParticipantApproved({
            packageUserGuid: data.packageUserGuid,
            deviceCode: data.sessionId,
          })
        );
      });

      this.signalRService.hubConnection.on('ReceiveParticipantApproval', (data) => {
        this.store.dispatch(
          ParticipantVerificationActions.ParticipantApproved({
            packageUserGuid: data.packageUserGuid,
          })
        );
      });

      this.signalRService.hubConnection.on('ReceiveSessionUserAdd', (data) => {
        this.store.dispatch(
          ParticipantVerificationActions.RemoveParticipantFromDevice({
            payload: {
              packageUserGuid: data.packageUserGuid,
              sessionId: data.sessionId,
            },
          })
        );
      });

      this.signalRService.hubConnection.on('ReceiveParticipantVerificationUpdated', (data) => {
        this.store.dispatch(
          ParticipantVerificationActions.ParticipantVerificationDetailsUpdated({
            packageUserGuid: data.packageUserGuid,
            deviceCode: data.sessionId,
            originalDeviceCode: data.originalSessionId
          })
        );
      });
    }
  }
}
